import { isMobile } from 'react-device-detect'
import {
  BaseApiRoute,
  BaseRoute,
  jsonApiGetRequest,
  jsonApiPostRequest,
  webstorePortalType,
} from './baseApiService'
import { runExclusive, CheckoutTimeoutMs } from './basketMutationQueue'

export const BasketContextBrowsing = 0
export const BasketContextCheckout = 1
export const CertificateOfComformitySku = 'COC'
export const StandardOrderOrigin = 0
export const DataWarehouseOrderOrigin = 1
export const MaxBasketItemQuantity = 100000

export const defaultPricingDisplayOptions = {
  showDeliveryOnRequest: false,
  showLoginForPrice: false,
  showStockLevelIndicator: false,
  showRequestAQuote: true,
  showListPrice: false,
  showNetPrice: false,
  showDeliveryDate: false,
  showIsFallbackMessage: false,
  showPricingTiers: false,
  showDiscardConfiguration: false,
  showFindDistributor: false,
}

export function whoAmI(requestId) {
  //logTrace('Sending WhoAmI request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/whoami`, {
    requestId: requestId,
  })
}

export function getGeolocationCountry(requestId) {
  //logTrace('Sending getGeolocation request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/getGeolocation`, {
    requestId: requestId,
  })
}

export function getProducts(
  urlParams,
  friendlyFilterParams,
  callingCountry,
  viewSize,
  requestId,
  currentQueryParams,
  locale
) {
  //logTrace('Sending getProducts request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}products/getProducts`, {
    friendlyFilterParams: friendlyFilterParams,
    urlParams: urlParams,
    callingCountry: callingCountry,
    isoLocalDateTime: new Date().toISOString(),
    device: isMobile ? 'mobile' : 'desktop',
    viewSize: viewSize,
    requestId: requestId,
    currentQueryParams: currentQueryParams,
    language: locale,
  })
}

export function register(registerModel, recaptchaCode, requestId) {
  //logTrace('Sending Register request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/register`, {
    firstName: registerModel.firstname,
    lastName: registerModel.lastname,
    companyName: registerModel.companyname,
    countryCode: registerModel.country,
    currency: registerModel.currency,
    email: registerModel.email,
    confirmEmail: registerModel.confirmemail,
    telephone: registerModel.telephone,
    recaptchaCode: recaptchaCode,
    country: registerModel.country,
    language: registerModel.language,
    password: registerModel.password,
    confirmPassword: registerModel.confirmpassword,
    requestId: requestId,
  })
}

export function registerTradeAccount(model, recaptchaCode, requestId) {
  return jsonApiPostRequest(`${BaseApiRoute}account/register-trade-account`, {
    companyName: model.companyname,
    accountNumber: model.accountnumber,
    countryCode: model.country,
    firstName: model.firstname,
    lastName: model.lastname,
    email: model.email,
    confirmEmail: model.confirmemail,
    telephone: model.telephone,
    password: model.password,
    confirmPassword: model.confirmpassword,
    recaptchaCode: recaptchaCode,
    language: model.language,
    requestId: requestId,
  })
}

export function search(
  urlParams,
  extrabaseUrlParams,
  friendlyFilterParams,
  callingCountry,
  viewSize,
  requestId,
  currentQueryParams,
  locale
) {
  //logTrace('Sending Search request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}search`, {
    urlParams: urlParams,
    extrabaseUrlParams: extrabaseUrlParams,
    friendlyFilterParams: friendlyFilterParams,
    callingCountry: callingCountry,
    isoLocalDateTime: new Date().toISOString(),
    device: isMobile ? 'mobile' : 'desktop',
    viewSize: viewSize,
    requestId: requestId,
    currentQueryParams: currentQueryParams,
    language: locale,
  })
}

export function searchV2(
  requestId,
  searchTerm,
  pageSize,
  filters,
  callingCountry,
  locale,
  currentPage = 0,
  preFilters = null,
  hiddenFilters = null
) {
  //logTrace('Sending Search V2 request. RequestId: ' + requestId)
  let userSelectedFacets = []
  let hiddenFacets = []

  if (Array.isArray(filters)) {
    userSelectedFacets = filters
  } else {
    // Convert filters object to SelectedFacets array format expected by backend
    const convertedFacets = Object.entries(filters).flatMap(([facetKey, facetValues]) =>
      facetValues.map((facetValue) => ({
        FacetKey: facetKey,
        FacetValue: facetValue,
      }))
    )
    userSelectedFacets = convertedFacets
  }

  if (hiddenFilters) {
    if (Array.isArray(hiddenFilters)) {
      hiddenFacets = hiddenFilters
    } else {
      // Convert hiddenFilters object to HiddenFacets array format expected by backend
      const convertedFacets = Object.entries(hiddenFilters).flatMap(([facetKey, facetValues]) =>
        facetValues.map((facetValue) => ({
          FacetKey: facetKey,
          FacetValue: facetValue,
        }))
      )
      hiddenFacets = convertedFacets
    }
  }

  const allSelectedFacets = userSelectedFacets.concat(preFilters)

  return jsonApiPostRequest(`${BaseApiRoute}v2/search`, {
    query: searchTerm,
    requestId: requestId,
    pageSize: pageSize || 12,
    page: currentPage,
    selectedFacets: allSelectedFacets,
    callingCountry: callingCountry,
    isoLocalDateTime: new Date().toISOString(),
    device: isMobile ? 'mobile' : 'desktop',
    language: locale,
    hiddenFacets: hiddenFacets,
  })
}

// Extended catalogue "view all" results (D-2-375, task-118). Mirrors the searchV2
// request body but targets the dedicated v2/searchextendedcatalogue endpoint; the
// response is a SearchV2ResponseViewModel whose `extendedCatalogue` collection
// carries the hits (other collections are empty for this endpoint).
export function searchExtendedCatalogue(
  requestId,
  searchTerm,
  pageSize,
  filters,
  callingCountry,
  locale,
  currentPage = 0,
  preFilters = null,
  hiddenFilters = null
) {
  let userSelectedFacets = []
  let hiddenFacets = []

  if (Array.isArray(filters)) {
    userSelectedFacets = filters
  } else if (filters) {
    userSelectedFacets = Object.entries(filters).flatMap(([facetKey, facetValues]) =>
      facetValues.map((facetValue) => ({
        FacetKey: facetKey,
        FacetValue: facetValue,
      }))
    )
  }

  if (hiddenFilters) {
    if (Array.isArray(hiddenFilters)) {
      hiddenFacets = hiddenFilters
    } else {
      hiddenFacets = Object.entries(hiddenFilters).flatMap(([facetKey, facetValues]) =>
        facetValues.map((facetValue) => ({
          FacetKey: facetKey,
          FacetValue: facetValue,
        }))
      )
    }
  }

  const allSelectedFacets = preFilters ? userSelectedFacets.concat(preFilters) : userSelectedFacets

  return jsonApiPostRequest(`${BaseApiRoute}v2/searchextendedcatalogue`, {
    query: searchTerm,
    requestId: requestId,
    pageSize: pageSize || 24,
    page: currentPage,
    selectedFacets: allSelectedFacets,
    callingCountry: callingCountry,
    isoLocalDateTime: new Date().toISOString(),
    device: isMobile ? 'mobile' : 'desktop',
    language: locale,
    hiddenFacets: hiddenFacets,
  })
}

// Document "view all" results (D-2-375, task-119). Mirrors the searchV2 request
// body but targets the dedicated v2/searchdocuments endpoint; the response is a
// SearchV2ResponseViewModel whose `documents` collection carries the hits
// ({ id, description, url, position }) — other collections are empty.
export function searchDocuments(
  requestId,
  searchTerm,
  pageSize,
  filters,
  callingCountry,
  locale,
  currentPage = 0,
  preFilters = null,
  hiddenFilters = null
) {
  let userSelectedFacets = []
  let hiddenFacets = []

  if (Array.isArray(filters)) {
    userSelectedFacets = filters
  } else if (filters) {
    userSelectedFacets = Object.entries(filters).flatMap(([facetKey, facetValues]) =>
      facetValues.map((facetValue) => ({
        FacetKey: facetKey,
        FacetValue: facetValue,
      }))
    )
  }

  if (hiddenFilters) {
    if (Array.isArray(hiddenFilters)) {
      hiddenFacets = hiddenFilters
    } else {
      hiddenFacets = Object.entries(hiddenFilters).flatMap(([facetKey, facetValues]) =>
        facetValues.map((facetValue) => ({
          FacetKey: facetKey,
          FacetValue: facetValue,
        }))
      )
    }
  }

  const allSelectedFacets = preFilters ? userSelectedFacets.concat(preFilters) : userSelectedFacets

  return jsonApiPostRequest(`${BaseApiRoute}v2/searchdocuments`, {
    query: searchTerm,
    requestId: requestId,
    pageSize: pageSize || 24,
    page: currentPage,
    selectedFacets: allSelectedFacets,
    callingCountry: callingCountry,
    isoLocalDateTime: new Date().toISOString(),
    device: isMobile ? 'mobile' : 'desktop',
    language: locale,
    hiddenFacets: hiddenFacets,
  })
}

// Content / page "view all" results (D-2-375, task-120). Mirrors the searchV2
// request body but targets the dedicated v2/searchcontent endpoint; the response
// is a SearchV2ResponseViewModel whose `content` collection carries the hits
// ({ id, name, url, type, imageUrl, position }) — other collections are empty.
// `type` distinguishes a blog post from a generic content/landing page.
export function searchContent(
  requestId,
  searchTerm,
  pageSize,
  filters,
  callingCountry,
  locale,
  currentPage = 0,
  preFilters = null,
  hiddenFilters = null
) {
  let userSelectedFacets = []
  let hiddenFacets = []

  if (Array.isArray(filters)) {
    userSelectedFacets = filters
  } else if (filters) {
    userSelectedFacets = Object.entries(filters).flatMap(([facetKey, facetValues]) =>
      facetValues.map((facetValue) => ({
        FacetKey: facetKey,
        FacetValue: facetValue,
      }))
    )
  }

  if (hiddenFilters) {
    if (Array.isArray(hiddenFilters)) {
      hiddenFacets = hiddenFilters
    } else {
      hiddenFacets = Object.entries(hiddenFilters).flatMap(([facetKey, facetValues]) =>
        facetValues.map((facetValue) => ({
          FacetKey: facetKey,
          FacetValue: facetValue,
        }))
      )
    }
  }

  const allSelectedFacets = preFilters ? userSelectedFacets.concat(preFilters) : userSelectedFacets

  return jsonApiPostRequest(`${BaseApiRoute}v2/searchcontent`, {
    query: searchTerm,
    requestId: requestId,
    pageSize: pageSize || 24,
    page: currentPage,
    selectedFacets: allSelectedFacets,
    callingCountry: callingCountry,
    isoLocalDateTime: new Date().toISOString(),
    device: isMobile ? 'mobile' : 'desktop',
    language: locale,
    hiddenFacets: hiddenFacets,
  })
}

export function suggestClicked(productId, queryId, objectPosition, index, requestId) {
  //logTrace('Sending SuggestClicked request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}v2/event/suggestClicked`, {
    productId: productId,
    queryId: queryId,
    objectPosition: objectPosition,
    index: index,
    requestId: requestId,
  })
}

export function searchClicked(
  productId,
  queryId,
  objectPosition,
  index,
  requestId,
  currency,
  country,
  language,
  timezoneOffsetMinutesToUtc,
  portalType
) {
  //logTrace('Sending SearchClicked request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}v2/event/searchClicked`, {
    productId: productId,
    queryId: queryId,
    objectPosition: objectPosition,
    index: index,
    requestId: requestId,
    currency: currency,
    country: country,
    language: language,
    timezoneOffsetMinutesToUtc: timezoneOffsetMinutesToUtc,
    portalType: portalType,
  })
}

export function suggest(term, locale, callingCountry, requestId) {
  //logTrace('Sending Suggest request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}suggest`, {
    suggestTerm: term,
    language: locale,
    callingCountry: callingCountry,
    requestId: requestId,
  })
}

export function suggestV2(term, locale, callingCountry, requestId) {
  //logTrace('Sending SuggestV2 request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}v2/suggest`, {
    suggestTerm: term,
    language: locale,
    callingCountry: callingCountry,
    requestId: requestId,
    device: isMobile ? 'mobile' : 'desktop',
  })
}

export function searchOverview(searchTerm, callingCountry, currency, locale, requestId) {
  return jsonApiPostRequest(`${BaseApiRoute}v2/searchoverview`, {
    searchTerm: searchTerm,
    requestId: requestId,
    currency: currency,
    callingCountry: callingCountry,
    language: locale,
    device: isMobile ? 'mobile' : 'desktop',
  })
}

export function getFeatures(requestId) {
  //logTrace('Sending getFeatures request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}features`, {
    requestId: requestId,
  })
}

export function createBasket(currency, country, language, requestId) {
  //logTrace('Sending createBasket request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}basket/create`, {
    currency: currency,
    requestId: requestId,
    country: country,
    language: language,
  })
}

export function getBasket(basketId, currency, country, requestId, basketContext, language) {
  //logTrace('Sending getBasket request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}basket/get`, {
    basketId: basketId,
    currency: currency,
    country: country,
    requestId: requestId,
    basketContext: basketContext,
    language: language,
  })
}

// No basketContext: the estimate is inherently a Browsing concern and the view model
// (GetBasketDeliveryEstimateRequestViewModel) does not bind the field - sending it was
// silently dropped by the model binder (PR #2196 review round 2).
export function getBasketDeliveryEstimate(basketId, currency, country, requestId, language) {
  return jsonApiPostRequest(`${BaseApiRoute}basket/getBasketDeliveryEstimate`, {
    basketId: basketId,
    currency: currency,
    country: country,
    requestId: requestId,
    language: language,
  })
}

export function getRecommendedProducts(
  basketId,
  currency,
  country,
  requestId,
  basketContext,
  language
) {
  //logTrace('Sending getRecommendedProducts request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}basket/getRecommendedProducts`, {
    basketId: basketId,
    currency: currency,
    country: country,
    requestId: requestId,
    basketContext: basketContext,
    language: language,
  })
}

export function addProductToBasket(
  basketId,
  itemSku,
  quantity,
  country,
  currency,
  requestId,
  basketContext,
  address,
  name,
  language,
  searchQuery,
  portSizes
) {
  //logTrace('Sending AddProductToBasket request. RequestId: ' + requestId)
  // PR #2212 review round 1 (1): a CHECKOUT-context add (the Certificate of Conformity tick)
  // runs the same whole-cart tax strip+re-add on the backend as applyDeliveryCharge, so it
  // joins the same per-basket chain - ticking CoC while the entry apply is in flight was the
  // measured doubling shape with a different trigger. Browsing adds stay unqueued on purpose:
  // a Browsing read runs no tax pass, and serialising every browse-side add would slow the
  // high-traffic add flows for no correctness gain.
  const postAddProduct = (signal) =>
    jsonApiPostRequest(`${BaseApiRoute}basket/addProduct`, {
      basketId: basketId,
      itemSku: itemSku,
      quantity: quantity,
      country: country,
      currency: currency,
      requestId: requestId,
      basketContext: basketContext,
      address: address,
      name: name,
      language: language,
      index: searchQuery ? searchQuery.index : null,
      searchQuery: searchQuery ? searchQuery.query : null,
      objectId: searchQuery ? searchQuery.objectId : null,
      portSizes: portSizes ?? null,
    }, false, signal)
  return basketContext === BasketContextCheckout
    ? runExclusive(basketId, postAddProduct)
    : postAddProduct()
}

export function addMultipleProductsToBasketJob(
  products,
  country,
  currency,
  requestId,
  language,
  addToBasketWidget
) {
  //logTrace('Sending MultipleProductsToBasketJob request')
  return jsonApiPostRequest(`${BaseApiRoute}job/createAddMultipleProductsToBasketJob`, {
    basketProducts: products,
    country: country,
    currency: currency,
    requestId: requestId,
    language: language,
    addToBasketWidget: addToBasketWidget,
  })
}

export function addMultipleProductsToBasket(
  basketId,
  basketProducts,
  country,
  currency,
  requestId,
  basketContext,
  address,
  name,
  language,
  addToBasketWidget
) {
  //logTrace('Sending AddProductToBasket request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}basket/addMultipleProducts`, {
    basketId: basketId,
    basketProducts: basketProducts,
    country: country,
    currency: currency,
    requestId: requestId,
    basketContext: basketContext,
    address: address,
    name: name,
    language: language,
    addToBasketWidget: addToBasketWidget,
  })
}

export function removeProductFromBasket(
  basketId,
  itemId,
  country,
  currency,
  requestId,
  basketContext,
  address,
  language
) {
  //logTrace('Sending RemoveProductFromBasket request. RequestId: ' + requestId)
  // PR #2212 review round 1 (1): Checkout-context removes (unticking CoC) join the per-basket
  // chain for the same reason as the add above; Browsing removes stay unqueued.
  const postRemoveProduct = (signal) =>
    jsonApiPostRequest(`${BaseApiRoute}basket/removeProduct`, {
      basketId: basketId,
      itemId: itemId,
      country: country,
      currency: currency,
      requestId: requestId,
      basketContext: basketContext,
      address: address,
      language: language,
    }, false, signal)
  return basketContext === BasketContextCheckout
    ? runExclusive(basketId, postRemoveProduct)
    : postRemoveProduct()
}

export function emptyBasket(basketId, country, currency, language, requestId) {
  //logTrace('Sending EmptyBasket request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}basket/emptyBasket`, {
    basketId: basketId,
    country: country,
    currency: currency,
    requestId: requestId,
    language: language,
  })
}

export function updateProduct(basketId, itemId, quantity, country, currency, language, requestId) {
  //logTrace('Sending UpdateProduct request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}basket/updateProduct`, {
    basketId: basketId,
    itemId: itemId,
    quantity: quantity,
    country: country,
    currency: currency,
    requestId: requestId,
    language: language,
  })
}

export function applyPromoCode(basketId, promoCode, country, currency, language, requestId) {
  //logTrace('Sending ApplyPromoCode request. RequestId: ' + requestId)
  // Same per-basket chain as applyDeliveryCharge: a promo apply in the Checkout context runs
  // the same whole-cart strip+re-add tax pass on the backend, so it is the same overlap class.
  return runExclusive(basketId, (signal) =>
    jsonApiPostRequest(`${BaseApiRoute}basket/applyPromoCode`, {
      basketId: basketId,
      promoCode: promoCode,
      country: country,
      currency: currency,
      requestId: requestId,
      language: language,
    }, false, signal)
  )
}

export function removePromoCode(
  basketId,
  promoCode,
  country,
  currency,
  language,
  requestId,
  basketContext,
  address
) {
  //logTrace('Sending RemovePromoCode request. RequestId: ' + requestId)
  // Removes a coupon by its CODE (un-redeems it) rather than deleting the
  // promotion_item line — EP re-materialises a line-deleted coupon. portalType/XSRF
  // are added by the shared client. basketContext/address must be sent (see
  // addProductToBasket) — omitting them binds basketContext to Browsing on the
  // backend, which deletes the delivery line and strips tax on the closing read.
  // Serialised per basket like applyDeliveryCharge: a Checkout-context remove runs the same
  // whole-cart strip+re-add tax pass, so it is the same overlap class.
  return runExclusive(basketId, (signal) =>
    jsonApiPostRequest(`${BaseApiRoute}basket/removePromoCode`, {
      basketId: basketId,
      promoCode: promoCode,
      country: country,
      currency: currency,
      requestId: requestId,
      language: language,
      basketContext: basketContext,
      address: address,
    }, false, signal)
  )
}

export function applyDeliveryCharge(
  basketId,
  address,
  country,
  currency,
  deliveryChargeId,
  modeOfTransport,
  language,
  requestId
) {
  //logTrace('Sending ApplyDeliveryCharge request. RequestId: ' + requestId)
  // Serialised per basket (see basketMutationQueue.js - the measured staging tax doubling):
  // the checkout step fires this twice on entry (address effect + delivery-option effect),
  // and two concurrent strip+re-add tax passes on one cart double every line's tax items.
  // The queue makes the second call WAIT, which is the proven-safe sequential shape, and
  // guarantees the freshest response is the last one written into the basket context.
  // This is the single choke point: v1 (checkoutPage template) and both v2 checkouts all
  // call through here.
  return runExclusive(basketId, (signal) =>
    jsonApiPostRequest(`${BaseApiRoute}basket/applyDeliveryCharge`, {
      basketId: basketId,
      address: address,
      country: country,
      currency: currency,
      deliveryChargeId: deliveryChargeId,
      deliveryModeOfTransport: modeOfTransport,
      language: language,
      requestId: requestId,
    }, false, signal)
  )
}

export function getAdditionalDeliveryOptions(basketId, address, country, currency, requestId) {
  //logTrace('Sending getAdditionalDeliveryOptions request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}basket/getAdditionalDeliveryOptions`, {
    basketId: basketId,
    address: address,
    country: country,
    currency: currency,
    requestId: requestId,
  })
}

export function removeDeliveryCharge(basketId, country, currency, language, requestId) {
  //logTrace('Sending RemoveDeliveryCharge request. RequestId: ' + requestId)
  // Same per-basket chain as applyDeliveryCharge: fired by the same UI transition (clearing
  // the address) and mutates the same delivery line + tax items, so it must never overlap
  // an in-flight apply.
  return runExclusive(basketId, (signal) =>
    jsonApiPostRequest(`${BaseApiRoute}basket/removeDeliveryCharge`, {
      basketId: basketId,
      country: country,
      currency: currency,
      language: language,
      requestId: requestId,
    }, false, signal)
  )
}

export function checkoutBasket(
  basketId,
  contact,
  billingAddress,
  shippingAddress,
  payment,
  delivery,
  requestId
) {
  //logTrace('Sending CheckoutBasket request. RequestId: ' + requestId)
  // PR #2212 review round 2 (2): Place Order joins the per-basket chain so it can never START
  // while a queued delivery/promo/CoC mutation is still stripping and re-adding tax - the
  // placement then runs against settled cart state. Round 3 (#187 (2)): it carries the signal
  // with the LONGER CheckoutTimeoutMs rather than no timeout at all - a fully exempt checkout
  // that stalled wedged every later mutation on the basket until a page reload. Beyond 120s
  // the outcome is unknowable client-side exactly as in today's browser-timeout case, and the
  // page's existing failure path owns that ambiguity.
  return runExclusive(
    basketId,
    (signal) =>
      jsonApiPostRequest(`${BaseApiRoute}basket/checkout`, {
        basketId: basketId,
        contact: contact,
        billingAddress: billingAddress,
        shippingAddress: shippingAddress,
        payment: payment,
        delivery: delivery,
        requestId: requestId,
      }, false, signal),
    CheckoutTimeoutMs
  )
}

export function preparePayment(
  basketId,
  contact,
  billingAddress,
  shippingAddress,
  payment,
  delivery,
  successUrl,
  errorUrl,
  language,
  requestId
) {
  //logTrace('Sending Payment Prepare request. RequestId: ' + requestId)
  // PR #2212 review round 2 (2) + round 3: same as checkoutBasket - prepare runs against
  // settled cart state, with the longer CheckoutTimeoutMs instead of a full exemption.
  return runExclusive(
    basketId,
    (signal) =>
      jsonApiPostRequest(`${BaseRoute}payment/prepare`, {
    basketId: basketId,
    contact: contact,
    billingAddress: billingAddress,
    shippingAddress: shippingAddress,
    payment: payment,
    delivery: delivery,
    successUrl: successUrl,
    errorUrl: errorUrl,
    language: language,
    requestId: requestId,
      }, false, signal),
    CheckoutTimeoutMs
  )
}

export function submitStripePaymentResult(
  cancelled,
  lookupIdentifier,
  paymentIntentId,
  paymentIntentClientSecret,
  requestId
) {
  return jsonApiPostRequest(`${BaseRoute}payment/stripe-result`, {
    cancelled: cancelled,
    lookupIdentifier: lookupIdentifier,
    paymentIntentId: paymentIntentId,
    paymentIntentClientSecret: paymentIntentClientSecret,
    requestId: requestId,
  })
}

export function getProduct(productCode, country, currency, requestId) {
  //logTrace('Sending GetProduct request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}product/getProduct`, {
    productCode: productCode,
    currency: currency,
    country: country,
    requestId: requestId,
  })
}

export function getCertificateOfConformityProduct(country, currency, requestId) {
  //logTrace('Sending GetCertificateOfConformityProduct request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}product/getCertificateOfConformityProduct`, {
    currency: currency,
    country: country,
    requestId: requestId,
  })
}

export function getPriceForDetailPage(productCode, country, currency, language, requestId) {
  //logTrace('Sending getPriceForDetailPage request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}product/getPriceForDetailPage`, {
    productCode: productCode,
    currency: currency,
    country: country,
    language: language,
    requestId: requestId,
  })
}

export function getPriceForListerPage(productCode, country, currency, language, requestId) {
  //logTrace('Sending getPriceForListerPage request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}product/getPriceForListerPage`, {
    productCode: productCode,
    currency: currency,
    country: country,
    language: language,
    requestId: requestId,
  })
}

export function getPriceForAccessoriesPage(productCode, country, currency, language, requestId) {
  //logTrace('Sending getPriceForAccessoriesPage request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}product/getPriceForAccessoriesPage`, {
    productCode: productCode,
    currency: currency,
    country: country,
    language: language,
    requestId: requestId,
  })
}

export function getAccessoriesByCategory(productId, group, currency, country, language, requestId) {
  return jsonApiPostRequest(`${BaseApiRoute}products/accessories/by-category`, {
    productId,
    groups: [group],
    currency,
    country,
    language,
    requestId,
  })
}

export function getAccountAddresses(requestId) {
  //logTrace('Sending GetAccountAddresses request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/getAccountAddresses`, {
    requestId: requestId,
  })
}

/**
 * Fetches checkout information for the current account.
 *
 * D2-168 / docket webstore_frontend #13: the backend response now includes
 * two additive fields used by the one-page credit-card checkout to
 * distinguish first-order US credit-card customers from returning ones.
 * These are pass-through — no wrapper-side transformation is applied.
 *
 * Response shape (additive fields only):
 * - `hasJdeAccount` {boolean} — `true` when the account already has a JDE
 *   account. `false` only for US credit-card accounts that have never
 *   placed an order. `true` for trade/ERP and non-US credit-card accounts.
 * - `existingBillingAddress` {?{ companyName, addressLine1, addressLine2,
 *   city, state, postcode, country }} — read-only JDE billing snapshot,
 *   populated only for returning US credit-card accounts; `null` otherwise.
 */
export function getAccountCheckoutInformation(basketId, checkoutSessionId, requestId) {
  //logTrace('Sending GetAccountCheckoutInformation request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/getAccountCheckoutInformation`, {
    basketId: basketId,
    checkoutSessionId: checkoutSessionId,
    requestId: requestId,
  })
}

export function addAccountAddress(address, requestId, basketContext = BasketContextBrowsing) {
  //logTrace('Sending AddAccountAddress request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/saveAccountAddress`, {
    address: address,
    requestId: requestId,
    basketContext: basketContext,
  })
}

export function deleteAccountAddress(addressId, requestId) {
  //logTrace('Sending deleteAccountAddress request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/deleteAccountAddress`, {
    addressId: addressId,
    requestId: requestId,
  })
}

export function changePassword(requestId) {
  //logTrace('Sending ChangePassword request. RequestId:' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/changePassword`, {
    requestId: requestId,
  })
}

export function getAllUserAccounts(requestId) {
  //logTrace('Sending GetAllUserAccounts request. RequestId:' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/getAllUserAccounts`, {
    requestId: requestId,
  })
}
export function getOrders(skip, take, orderOrigin, requestId) {
  //logTrace('Sending GetOrders request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}order/getPaged`, {
    skip: skip,
    take: take,
    orderOrigin: orderOrigin,
    requestId: requestId,
  })
}

export function changeAccount(accountId, requestId) {
  //logTrace('Sending ChangeAccount request. RequestId:' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/changeAccount`, {
    requestId: requestId,
    accountId: accountId,
  })
}

export function getOrder(orderId, orderOrigin, requestId) {
  //logTrace('Sending GetOrders request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}order/get`, {
    orderId: orderId,
    orderOrigin: orderOrigin,
    requestId: requestId,
  })
}

export function getOrderForCheckout(orderId, orderOrigin, requestId) {
  //logTrace('Sending GetOrders request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}order/getForCheckout`, {
    orderId: orderId,
    orderOrigin: orderOrigin,
    requestId: requestId,
  })
}

export function findAddress(text, container, country, requestId) {
  //logTrace('Sending FindAddress request. RequestID: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/findaddress`, {
    text: text,
    container: container,
    country: country,
    requestId: requestId,
  })
}

export function retrieveAddress(id, requestId) {
  //logTrace('Sending FindAddress request. RequestID: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}account/retrieveAddress`, {
    id: id,
    requestId: requestId,
  })
}

export function submitFormToSalesForce(data, submitUrl, recaptchaCode, requestId) {
  //logTrace('Sending submitFormToSalesForce request. Request: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}form/submitForm`, {
    requestId: requestId,
    formData: data,
    submitUrl: submitUrl,
    recaptchaCode: recaptchaCode,
  })
}

export function getSavedBasekts(requestId, country, currency, language) {
  //logTrace('Sending GetSavedBaskestsRequest. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}savedbasket/getSavedBaskets`, {
    requestId: requestId,
    currency: currency,
    language: language,
    country: country,
  })
}

export function saveCurrentBasket(requestId, country, currency, language, basketId, basketName) {
  //logTrace('Sending SaveCurrentBasket request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}savedbasket/saveCurrentBasket`, {
    requestId: requestId,
    currency: currency,
    language: language,
    country: country,
    basketId: basketId,
    basketName: basketName,
  })
}

export function replaceCurrentBasketWithSavedBasket(
  requestId,
  country,
  currency,
  language,
  savedBasketId
) {
  //logTrace('Sending SaveCurrentBasket request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}savedbasket/replaceCurrentBasketWithSavedBasket`, {
    requestId: requestId,
    currency: currency,
    language: language,
    country: country,
    savedBasketId: savedBasketId,
  })
}

export function mergeCurrentBasketWithSavedBasket(
  requestId,
  country,
  currency,
  language,
  savedBasketId
) {
  //logTrace('Sending SaveCurrentBasket request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}savedbasket/mergeCurrentBasketWithSavedBasketJob`, {
    requestId: requestId,
    currency: currency,
    language: language,
    country: country,
    savedBasketId: savedBasketId,
  })
}

export function addOrderItemsToBasketJob(requestId, country, currency, language, orderId) {
  //logTrace('Sending addOrderItemsToBasketJob request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}savedbasket/addOrderItemsToBasketJob`, {
    requestId: requestId,
    currency: currency,
    language: language,
    country: country,
    orderId: orderId,
  })
}

export function deleteSavedBasket(requestId, country, currency, language, basketId) {
  //logTrace('Sending DeleteSavedBasket request. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}savedbasket/deleteSavedBasket`, {
    requestId: requestId,
    currency: currency,
    language: language,
    country: country,
    basketId: basketId,
  })
}

export function submitForm(data, url) {
  data = { ...data, portalType: webstorePortalType }
  const formBody = Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&')

  return new Promise((resolve, reject) => {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formBody,
    })
      .then((response) => {
        switch (response.status) {
          case 200:
            resolve({ status: response.status })
            break
          default:
            reject({ status: response.status })
            break
        }
      })
      .catch((error) => reject({ error: error }))
  })
}

export function getValidCurrencies(requestId) {
  //logTrace('Sending GetValidCurrencies request: Request Id: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}currency/getValidCurrencies`, {
    requestId: requestId,
  })
}

export function saveConfiguration(
  requestId,
  country,
  currency,
  language,
  shortCode,
  protocol,
  range,
  numberOfStations,
  description
) {
  //logTrace('Sending SaveConfiguration requeset: Request Id: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}configurator/saveConfiguration`, {
    country: country,
    requestId: requestId,
    currency: currency,
    language: language,
    shortCode: shortCode,
    protocol: protocol,
    range: range,
    numberOfStations: numberOfStations,
    description: description,
  })
}

export function getSavedConfigurations(requestId, country, currency, language) {
  //logTrace('Sending GetSavedConfigurations. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}configurator/getSavedConfigurations`, {
    requestId: requestId,
    currency: currency,
    language: language,
    country: country,
  })
}

export function deleteSavedConfiguration(requestId, country, currency, language, configurationId) {
  //logTrace('Sending DeleteSavedConfigurations. RequestId: ' + requestId)
  return jsonApiPostRequest(`${BaseApiRoute}configurator/deleteSavedConfiguration`, {
    requestId: requestId,
    currency: currency,
    language: language,
    country: country,
    savedConfigurationId: configurationId,
  })
}

export function downloadInvoice(requestId, invoiceNumber, invoiceDate) {
  return jsonApiPostRequest(
    `${BaseApiRoute}order/downloadInvoice`,
    {
      requestId: requestId,
      invoiceNumber: invoiceNumber,
      invoiceDate: invoiceDate,
    },
    true
  )
}

export function resolvePaymentRequest(token) {
  return jsonApiGetRequest(
    `${BaseApiRoute}payment-request/resolve?token=${encodeURIComponent(token)}`
  )
}

export function preparePaymentRequest(token) {
  return jsonApiPostRequest(`${BaseApiRoute}payment-request/prepare`, {
    token: token,
  })
}

export function resultPaymentRequest(token, paymentIntentId, requestId) {
  return jsonApiPostRequest(`${BaseApiRoute}payment-request/result`, {
    token: token,
    paymentIntentId: paymentIntentId,
    requestId: requestId,
  })
}
